"use client";
import { IResource } from "@gettactic/api";
import {
  InteractiveZone,
  InteractiveZonesInfo
} from "@gettactic/helpers/src/map/types";
import { canManageResource } from "@gettactic/helpers/src/permissions";
import { useAuthenticated } from "@gettactic/hooks";
import React from "react";
import { createPortal } from "react-dom";
import { MapPinRequestLayer } from "@/features/map/components/MapView/MapPinRequestLayer";
import { MapViewHoverDetails } from "@/features/map/components/MapView/overlays/MapViewHoverDetails";
import { ResourceDetails } from "@/features/map/components/ResourceDetails";
import { useIsMapPinRequestEnabled } from "@/features/map/hooks/useIsMapPinRequestEnabled";
import { useMap } from "@/features/map/MapProvider";
import { ResourceSettingsProvider } from "@/features/reserve/hooks/useResourceSettings";
import { useRoomSelection } from "@/features/reserve/providers/roomSelectionContext";
import { useDateFormat } from "@/lib/hooks/useDateFormat";

export interface MapViewOverlaysProps {
  svgMapRef?: React.RefObject<SVGSVGElement | null | undefined>;
  zonesInfo: InteractiveZonesInfo;
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
  /** When set, clicking "Reserve" in the resource popover hands the resource
   *  off to this callback instead of using this MapProvider instance's own
   *  startReserve — needed by embedded previews (e.g. the Home page live
   *  map) that don't mount a MapReserveCard to listen for it. */
  onReserveResource?: (resource: IResource) => void;
}

export const MapViewOverlays: React.FC<MapViewOverlaysProps> = ({
  svgMapRef,
  zonesInfo,
  mapContainerRef,
  onReserveResource
}) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const formatDate = useDateFormat();
  const isMapPinRequestEnabled = useIsMapPinRequestEnabled();
  const { isPickingRoom } = useRoomSelection();

  const [hoveringResource, setHoveringResource] =
    React.useState<IResource | null>(null);

  const [highlightUserId, setHighlightUserId] = React.useState<
    string | undefined
  >();

  const {
    floor,
    allResources: resources,
    startDate,
    endDate,
    selectedResource,
    setSelectedResource,
    isEmbedded,
    isMapBuilderMap,
    poppers,
    openResourceModal,
    setOpenResourceModal,
    editGroupId,
    setEditGroupId,
    events,
    setMapChangesCount,
    fullscreenMapDisclosure,
    isReservationMode,
    selectionMode,
    isPinRequestMode,
    startReserve
  } = useMap();

  React.useEffect(() => {
    const zones = zonesInfo.zones.reduce(
      (acc, zone) => {
        acc[zone.id] = zone;
        return acc;
      },
      {} as Record<string, InteractiveZone>
    );

    const onHighlightResource = (
      resource: IResource | null,
      userId?: string
    ) => {
      if (!resource || !zones[resource?.id || ""]) {
        setHoveringResource(null);
        setHighlightUserId(undefined);
        return;
      }

      setHoveringResource({ ...resource, ...zones[resource.id] });
      setHighlightUserId(userId);
    };
    events.register("onHighlightResource", onHighlightResource);

    return () => {
      events.unregister("onHighlightResource", onHighlightResource);
    };
  }, [zonesInfo.zones]);

  if (!authenticatedUser) return null;

  const formattedDate = formatDate(startDate);

  const allowResourceDetails =
    !selectionMode &&
    // In pin-request mode the map is for dropping a pin on an empty point, so a
    // resource click should not open the details popup.
    !isPinRequestMode &&
    // While picking a room to add, a resource click feeds the selection instead.
    !isPickingRoom &&
    floor &&
    startDate &&
    (svgMapRef?.current || isMapBuilderMap) &&
    currentOffice &&
    currentOffice.id;

  const meetingsDisabled =
    !authenticatedUser?.organization?.meeting_room_bookings_enabled;

  const openReservation = (resource: IResource) => {
    if (onReserveResource) {
      onReserveResource(resource);
      return;
    }
    // Use startReserve to properly open the reserve modal
    // This ensures the modal opens correctly even when closing fullscreen mode
    startReserve(resource);
  };

  return (
    <>
      {allowResourceDetails ? (
        <>
          {createPortal(
            <ResourceSettingsProvider>
              <ResourceDetails
                resourcePermission={canManageResource(
                  authenticatedUser,
                  selectedResource
                )}
                openResourceModal={openResourceModal}
                setOpenResourceModal={setOpenResourceModal}
                startDate={startDate}
                endDate={endDate}
                formattedDate={formattedDate}
                openReservation={openReservation}
                res={selectedResource}
                floor={floor}
                zones={isMapBuilderMap ? [] : zonesInfo.zones}
                resources={resources || []}
                editGroupId={editGroupId}
                svg={svgMapRef?.current!}
                officeId={currentOffice.id}
                setEditGroupId={setEditGroupId}
                setMapChanges={setMapChangesCount}
                setPopperElement={poppers.setPopperResDetailsElement}
                styles={poppers.stylesResDetails}
                attributes={poppers.attributesResDetails}
                officeTimeZone={currentOffice.time_zone}
                meetingsDisabled={meetingsDisabled}
                embedded={isEmbedded}
                setSelectedResource={setSelectedResource}
                isReservationMode={isReservationMode}
                fullscreenMapDisclosure={fullscreenMapDisclosure}
              />
            </ResourceSettingsProvider>,
            window.document.body
          )}
        </>
      ) : null}
      <MapViewHoverDetails
        openReservation={openReservation}
        setResource={setSelectedResource}
        // Suppress the hover preview while the click card is open so the two
        // popovers don't stack on top of each other.
        resource={openResourceModal ? null : hoveringResource}
        floor={floor}
        zones={isMapBuilderMap ? [] : zonesInfo.zones}
        formattedDate={formattedDate}
        setPopperElement={poppers.setPopperDetailsHighlightElement}
        highlightUserId={highlightUserId}
        mapContainerRef={mapContainerRef}
        enablePortal
      />
      {isMapPinRequestEnabled && !selectionMode && !isReservationMode ? (
        <MapPinRequestLayer svgMapRef={svgMapRef} />
      ) : null}
    </>
  );
};
